import { Filter, Loader2, Plus, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import SearchResultsCard from '@/js/components/card/SearchResultsCard';
import beginSearchImage from '@/js/images/beginSearch.webp';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import { invokeBedrockModel } from '@/aws-config';
import {
  addShortlistedAction,
  clearSearchAction,
  setSearchResultAction,
} from '@/js/reduxstore/actions/searchActions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import CompareModal from '@/js/components/modals/CompareModal';

const SearchScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = createApiClient(navigate, dispatch);

  var { projectId, projectName } = useParams();
  const reduxSearchData = useSelector((state) => state?.searchReducer);
  const searchDescription = location.state?.searchDescription;

  const shortlisted = reduxSearchData?.shortlisted?.shortlisted;

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const [filteredResults, setFilteredResults] = useState(null);
  const [rawResults, setRawResults] = useState(null);

  const [searchResult, setSearchResult] = useState(null);
  const [bookmarks, setBookmarks] = useState(shortlisted);
  const keywordsInputRef = useRef(null);

  const [searchParams, setSearchParams] = useState({
    productDescription:
      searchDescription ||
      reduxSearchData?.searchParams?.productDescription ||
      '',
    materials: reduxSearchData?.searchParams?.materials || [],
    process: reduxSearchData?.searchParams?.process || [],
    certifications: reduxSearchData?.searchParams?.certifications || [],
    location: reduxSearchData?.searchParams?.location || [],
  });

  const [addParams, setAddParams] = useState({ state: false, field: '' });

  // compare
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedToCompare, setSelectedToCompare] = useState([]);
  const [compareError, setCompareError] = useState(null);

  const handleCompare = () => {
    if (selectedToCompare.length === 0) {
      setCompareError('Select Manufacturers to compare');
      return;
    }

    setCompareError(null);
    setShowCompareModal(true);
  };

  const toggleSelect = (item) => {
    const updatedSelectedToCompare = selectedToCompare.some(
      (manufacturer) => manufacturer.manufacturer_id === item.manufacturer_id,
    )
      ? selectedToCompare.filter(
          (manufacturer) =>
            manufacturer.manufacturer_id !== item.manufacturer_id,
        )
      : [...selectedToCompare, item];

    setSelectedToCompare(updatedSelectedToCompare);
  };

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;

    if (searchParams.productDescription !== '') {
      generateResults();
      hasFetchedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!rawResults) return;

    const { materials, process, certifications, location } = searchParams;

    const filterResults = rawResults.filter((item) => {
      const keywords = item.matched_keywords || {};

      const matchesMaterials = materials.every((mat) =>
        (keywords.materials || []).some(
          (k) => k.toLowerCase() === mat.toLowerCase(),
        ),
      );

      const matchesProcess = process.every((proc) =>
        (keywords.processes || []).some(
          (k) => k.toLowerCase() === proc.toLowerCase(),
        ),
      );

      const matchesCerts = certifications.every((cert) =>
        (keywords.certifications || []).some(
          (k) => k.toLowerCase() === cert.toLowerCase(),
        ),
      );

      const matchesLocation = location.every((loc) =>
        (item.country || '').toLowerCase().includes(loc.toLowerCase()),
      );

      return (
        matchesMaterials && matchesProcess && matchesCerts && matchesLocation
      );
    });

    setFilteredResults(filterResults);
    setSearchResult(filterResults);
    dispatch(setSearchResultAction(searchParams));
  }, [searchParams, rawResults]);

  useEffect(() => {
    if (!filteredResults) return;

    const sortedResults = [...filteredResults].sort((a, b) => {
      const aBookmarked = bookmarks.some(
        (bookmark) => bookmark.manufacturer_id === a.manufacturer_id,
      );
      const bBookmarked = bookmarks.some(
        (bookmark) => bookmark.manufacturer_id === b.manufacturer_id,
      );

      if (aBookmarked === bBookmarked) return 0;
      return aBookmarked ? -1 : 1;
    });

    setSearchResult(sortedResults);
  }, [bookmarks, filteredResults]);

  const handleToggleAddParams = (key) => {
    setAddParams((prevParams) => ({
      state: prevParams.field !== key ? true : !prevParams.state,
      field: prevParams.field !== key ? key : '',
    }));
  };

  const handleAddItem = (e, fieldName) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      if (e?.preventDefault) e.preventDefault();
      const value = e.target.value.trim();
      setSearchParams((prev) => {
        const updatedField = [...new Set([...prev[fieldName], value])];
        return { ...prev, [fieldName]: updatedField };
      });
      e.target.value = '';
    }
  };

  const handleRemoveItem = (field, index) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const toggleBookmark = (item) => {
    const updatedBookmarks = bookmarks.some(
      (bookmark) => bookmark.manufacturer_id === item.manufacturer_id,
    )
      ? bookmarks.filter(
          (bookmark) => bookmark.manufacturer_id !== item.manufacturer_id,
        )
      : [...bookmarks, item];

    if (updatedBookmarks.length > 0) {
      dispatch(
        addShortlistedAction({
          description: searchParams?.productDescription,
          shortlisted: updatedBookmarks,
        }),
      );
    }
    setBookmarks(updatedBookmarks);
  };

  // this is shifted to backend
  // const generateKeywordsFromDescription = async () => {
  //   if (!searchParams.productDescription.trim()) {
  //     setError('Please enter a product description first.');
  //     return;
  //   }

  //   setProcessing(true);
  //   try {
  //     const response = await invokeBedrockModel(
  //       searchParams.productDescription,
  //     );
  //     if (response) {
  //       const parsedData = JSON.parse(response?.content[0]?.text);
  //       setSearchParams((prevState) => ({
  //         ...prevState,
  //         materials: [
  //           ...new Set([
  //             ...prevState?.materials,
  //             ...(parsedData?.materials || []),
  //           ]),
  //         ],
  //         process: [
  //           ...new Set([
  //             ...prevState?.process,
  //             ...(parsedData?.processes || []),
  //           ]),
  //         ],
  //         certifications: [
  //           ...new Set([
  //             ...prevState?.certifications,
  //             ...(parsedData?.standards || []),
  //           ]),
  //         ],
  //         location: [
  //           ...new Set([
  //             ...prevState?.location,
  //             ...(parsedData?.location || []),
  //           ]),
  //         ],
  //       }));

  //       setError('');
  //     }
  //   } catch (error) {
  //     console.log(error);
  //     setError('Failed to generate keywords. Please try again later.');
  //   } finally {
  //     setProcessing(false);
  //   }
  // };

  const generateResults = async () => {
    setProcessing(true);
    setError('');
    if (!searchParams.productDescription.trim()) {
      setError('Please enter a product description first.');
      setProcessing(false);
      return;
    }

    try {
      const response = await apiClient.post('/api/manufacturers/search', {
        description: searchParams?.productDescription,
      });

      const results = response?.data?.data;
      setRawResults(results);
      setSearchResult(results);
      setError('');
      dispatch(setSearchResultAction(searchParams));
    } catch (error) {
      const errorMessage = error?.response?.data?.error_message;
      setError(errorMessage || 'Network error. Please try again later.');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewBookmarks = async () => {
    if (bookmarks.length === 0) {
      setCompareError('Please Shortlist Manufacturers');
      return;
    }
    setCompareError(null);
    if (projectId) {
      dispatch(
        addShortlistedAction({
          description: searchParams?.productDescription,
          shortlisted: bookmarks,
        }),
      );

      try {
        // getting project verseion id
        const response = await apiClient.post(
          '/api/project-part/get',
          JSON.stringify({
            project_id: projectId,
          }),
        );

        const manufacturerIds = bookmarks.map((manu) => manu.manufacturer_id);

        const shortlistResponse = await apiClient.post(
          '/api/shortlisted-manufacturer/create',
          JSON.stringify({
            project_id: projectId,
            project_version_id:
              response?.data?.data?.project?.project_version_id,
            part_ids: [],
            part_version_ids: [],
            manufacturer_ids: manufacturerIds,
          }),
        );

        console.log(
          '✅ Manufacturers shortlisted successfully:',
          shortlistResponse.data,
        );
      } catch (shortlistError) {
        // Log error but continue with the process
        console.log(
          shortlistError?.response?.data?.error_message || shortlistError,
        );
      }

      navigate(`/project/details/${projectName}/${projectId}`, {
        state: { switchTo: 'MANUFACTURERS' },
      });
    } else {
      if (bookmarks.length > 0) {
        dispatch(
          addShortlistedAction({
            description: searchParams?.productDescription,
            shortlisted: bookmarks,
          }),
        );
        navigate(`/project/details`, {
          state: { switchTo: 'MANUFACTURERS' },
        });
      } else {
        console.log('No Bookmarks');
      }
    }
  };

  const resetSearch = () => {
    setSearchParams({
      productDescription: '',
      materials: [],
      process: [],
      certifications: [],
      location: [],
    });
    setSearchResult(null);
    setRawResults(null);
    setFilteredResults(null);
    setError('');
    setAddParams({ state: false, field: '' });
    dispatch(clearSearchAction());
  };

  const renderFilterSection = (field) => (
    <div key={field.key} className="mb-6">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-gray-700 font-semibold">{field.label}</h3>
        <button
          className="p-1 hover:bg-gray-100 ease-in duration-200 rounded-full"
          onClick={() => handleToggleAddParams(field.key)}
        >
          {addParams.field !== field.key ? (
            <Plus size={16} className="rounded-full shadow-sm text-gray-700" />
          ) : (
            <X size={16} className="rounded-full shadow-sm text-gray-700" />
          )}
        </button>
      </div>
      {addParams.state && addParams.field === field.key && (
        <div className="relative mb-3">
          <Input
            placeholder={`Add ${field.label.toLowerCase()}`}
            onKeyDown={(e) => handleAddItem(e, field.key)}
            ref={keywordsInputRef}
            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
          />
          <button
            type="button"
            onClick={() =>
              handleAddItem(
                { key: 'Enter', target: keywordsInputRef.current },
                field.key,
              )
            }
            className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {searchParams[field.key].map((item, index) => (
          <div
            key={index}
            className="flex space-x-2 text-slate-500 px-2 py-1 rounded flex items-center border-[1px] border-veryLightPurple"
          >
            <span>{item}</span>
            <button
              className="text-gray-500"
              onClick={() => handleRemoveItem(field.key, index)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="font-roboto grid grid-cols-12 gap-x-6 min-h-[calc(100vh-70px)] overflow-hidden">
      {/* Sidebar */}
      <div
        className="col-span-3 bg-white p-4 rounded-lg shadow h-full max-h-screen overflow-y-auto"
        style={{ scrollbarWidth: 'none', scrollbarColor: '#B2BBC6 #F2F4F7' }}
      >
        <div className="flex flex-row justify-between items-center mb-2">
          <h3 className="text-gray-700 font-semibold">Product Description</h3>
          <button
            className="px-2 py-1 rounded text-sm text-darkPurple font-semibold border border-darkPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-300"
            onClick={resetSearch}
          >
            Reset
          </button>
        </div>

        <Textarea
          className="min-h-[100px] mb-8 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-400"
          placeholder="Enter product description"
          value={searchParams.productDescription}
          onChange={(e) => {
            setSearchParams({
              ...searchParams,
              productDescription: e.target.value,
            });
            setError('');
          }}
          style={{
            scrollbarWidth: 'none',
            scrollbarColor: '#b0b0b0 #f0f0f0',
          }}
          // onBlur={generateKeywordsFromDescription}
        />

        {/* <div className="my-4">
          <Button
            className="bg-darkPurple text-white w-full hover:bg-darkerPurple"
          >
            {processing ? (
              <Loader2 className="animate-spin h-5 w-5" />
            ) : (
              'Generate Keywords'
            )}
          </Button>
        </div> */}

        {/* Dynamic filter sections */}
        {[
          { label: 'Materials Required', key: 'materials' },
          { label: 'Manufacturing Process', key: 'process' },
          { label: 'Certifications', key: 'certifications' },
          { label: 'Location', key: 'location' },
        ].map(renderFilterSection)}

        {error && <p className="text-red-500 text-sm mt-">{error}</p>}

        {/* Generate Button */}
        <div className="mt-8">
          <Button
            className="bg-darkPurple hover:bg-darkerPurple text-white w-full"
            onClick={generateResults}
          >
            Generate Result
          </Button>
        </div>
      </div>

      {/* Main Results Section */}
      <div className="col-span-9 bg-white p-4 rounded-lg flex flex-col h-full max-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-bold text-lg">
            {searchResult?.length > 0 ? 'Manufacturers' : 'Search'}
          </h1>

          <div className="flex gap-3">
            {compareError && (
              <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-2 px-3 rounded-lg">
                {compareError}
              </p>
            )}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="text-darkPurple font-medium border border-darkPurple rounded px-2 hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200 py-1"
                    onClick={handleCompare}
                  >
                    Compare
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="bg-black text-white text-xs rounded px-2 py-1 rounded"
                >
                  to select click on any manufacturer card
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <button
              className="text-darkPurple font-medium border border-darkPurple rounded px-2 hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200 py-1"
              onClick={() => handleViewBookmarks()}
            >
              View Shortlisted {bookmarks && `(${bookmarks.length})`}
            </button>
          </div>
        </div>

        {/* Results */}
        <div
          className="grid grid-cols-3 gap-4 overflow-y-auto relative"
          style={{ scrollbarWidth: 'none', scrollbarColor: '#B2BBC6 #F2F4F7' }}
        >
          {processing && (
            <div className="col-span-3 flex flex-col justify-center items-center text-center flex-grow">
              <Loader2 className="h-32 w-32 animate-spin" />
              <p className="text-gray-600 text-sm max-w-md">Searching...</p>
            </div>
          )}
          {!processing && searchResult ? (
            searchResult.length > 0 ? (
              searchResult.map((result, index) => (
                <SearchResultsCard
                  key={index}
                  item={result}
                  index={index}
                  bookmarks={bookmarks}
                  toggleBookmark={toggleBookmark}
                  selectedToCompare={selectedToCompare}
                  toggleSelect={toggleSelect}
                />
              ))
            ) : (
              <div className="col-span-3 flex flex-col justify-center items-center text-center flex-grow">
                <h2 className="text-2xl font-semibold text-gray-800">
                  We are enrolling new Manufacturing Partners.
                </h2>
                <img
                  src={beginSearchImage}
                  className="w-56 h-48 my-4"
                  alt="Search Illustration"
                />
                <p className="text-gray-600 text-sm max-w-md">
                  Try to search for different keywords.
                </p>
              </div>
            )
          ) : (
            !processing && (
              <div className="col-span-3 flex flex-col justify-center items-center text-center flex-grow">
                <h2 className="text-2xl font-semibold text-gray-800">
                  Start Your Search for Trusted <br /> Manufacturing Partners.
                </h2>
                <img
                  src={beginSearchImage}
                  className="w-56 h-48 my-4"
                  alt="Search Illustration"
                />
                <p className="text-gray-600 text-sm max-w-md">
                  Use filters to narrow down manufacturers by process, location,
                  certifications, and more.
                </p>
              </div>
            )
          )}
        </div>
      </div>

      <CompareModal
        open={showCompareModal}
        bookmarks={bookmarks}
        toggleBookmark={toggleBookmark}
        selectedToCompare={selectedToCompare}
        toggleSelect={toggleSelect}
        onClose={() => {
          setShowCompareModal(false);
        }}
      />
    </div>
  );
};

export default SearchScreen;
